import React, { useCallback, useState } from "react";
import { Button, Card, Input, Modal, Spin } from "antd";
import { useParams } from "react-router-dom";
import { useGetDocumentsMutation } from "../../../store/api/lumi/documents";
import { LoadingOutlined } from "@ant-design/icons";
import { getFormattedDocument } from "../../../utils/Docs/DocUtils";
import { ParagraphList } from "./ParagraphList";
import Scrollbars from "react-custom-scrollbars-2";
import MetadataFields from "./MetadataFields";
import { useGetLLMPassThroughMutation } from "../../../store/api/lumi/llm";
import { useGetSentimentsDocsMutation } from "../../../store/api/lumi/sentiment";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { sentimentType } from "../../../constants";

const SentimentDocsTest = ({ concept, additionalFilter, include_sentiment_on_concepts, props }) => {
    const { id } = useParams();
    const [isVisible, setIsVisible] = useState(false);
    const [searchConcept, setSearchConcept] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [positiveDocuments, setPositiveDocuments] = useState([]);
    const [negativeDocuments, setNegativeDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [llmSummary, setLlmSummary] = useState({ positive: null, negative: null });
    const [positivePrompt, setPositivePrompt] = useState("");
    const [negativePrompt, setNegativePrompt] = useState("");
    const [documentLimit, setDocumentLimit] = useState(100);
    const [buttonLoading, setButtonLoading] = useState({ positive: false, negative: false });

    const [triggerGetSentiments] = useGetSentimentsDocsMutation();
    const [triggerGetDocuments] = useGetDocumentsMutation();
    const [triggerGetLLMPassThrough] = useGetLLMPassThroughMutation();

    const fetchDocumentsWithSentiment = useCallback(async () => {
        setLoading(true);
        const conceptFilter = getConceptFilter(props);

        try {
            const result = await triggerGetSentiments({
                id,
                filter: additionalFilter || [],
                search: concept.concept.texts,
                concept_selector: conceptFilter,
                include_sentiment_on_concepts: true,
            }).unwrap();

            const positive = result.filter((doc) => doc.sentiment === sentimentType.POSITIVE);
            const negative = result.filter((doc) => doc.sentiment === sentimentType.NEGATIVE);

            setPositiveDocuments(positive);
            setNegativeDocuments(negative);
        } catch (error) {
            console.error("Failed to fetch sentiment documents:", error);
            setPositiveDocuments([]);
            setNegativeDocuments([]);
        } finally {
            setLoading(false);
        }
    }, [concept.name, id, additionalFilter, props, triggerGetSentiments]);

    const fetchDocuments = useCallback(async () => {
        setLoading(true);

        try {
            const result = await triggerGetDocuments({
                id,
                filter: additionalFilter || [],
                search: concept.name,
                limit: 50,
                include_sentiment_on_concepts,
            }).unwrap();

            setDocuments(result.documents);
            setSearchConcept(result.concept);
            setDocumentLimit(result.documents.length);
        } catch (error) {
            console.error("Failed to fetch documents:", error);
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    }, [concept.name, id, additionalFilter, include_sentiment_on_concepts, triggerGetDocuments]);

    const handleLlmRequest = async (prompt, docs, type) => {
        if (!prompt || docs.length === 0) return;
        setButtonLoading((prev) => ({ ...prev, [type]: true }));
        const documentTexts = docs.slice(0, documentLimit).map((doc) => `<p>${doc.text}</p>`).join("");
        const fullPrompt = `${prompt}\n${documentTexts}`;

        try {
            const response = await triggerGetLLMPassThrough({
                projectId: id,
                prompt: fullPrompt,
            }).unwrap();

            setLlmSummary((prev) => ({ ...prev, [type]: response.response }));
        } catch (error) {
            console.error(`Failed to fetch LLM summary for ${type} documents:`, error);
        } finally {
            setButtonLoading((prev) => ({ ...prev, [type]: false }));
        }
    };

    const showModal = () => {
        setIsVisible(true);
        if (props) {
            fetchDocumentsWithSentiment();
        } else {
            fetchDocuments();
        }
    };

    const handleCancel = () => {
        setIsVisible(false);
        setDocuments([]);
        setPositiveDocuments([]);
        setNegativeDocuments([]);
        setLlmSummary({ positive: null, negative: null });
    };

    const renderDocumentList = (docs) => {
        return docs.slice(0, documentLimit).map((doc, index) => {
            const { paragraphList } = getFormattedDocument(doc, searchConcept, false);
            return (
                <Card
                    key={index}
                    title={doc.title || "Document Details"}
                    bordered={false}
                    style={{ width: 400, height: 350, margin: 10 }}
                >
                    <Scrollbars style={{ height: 250 }} autoHide>
                        <ParagraphList paragraphList={paragraphList} />
                        <MetadataFields metadata={doc.metadata} />
                    </Scrollbars>
                </Card>
            );
        });
    };

    const renderSummary = (type) => {
        const summary = llmSummary[type];
        if (!summary) return null;

        return (
            <div style={{ marginBottom: "1rem" }}>
                <h3>{type === "positive" ? "Positive Summary" : "Negative Summary"}</h3>
                <p>{summary}</p>
            </div>
        );
    };

    return (
        <div>
            <div onClick={showModal} className="overall-sentiment-concept">
                {concept.name}: {concept.count}
            </div>
            <Modal
                width="80%"
                title={`Details for "${concept.name}"`}
                open={isVisible}
                onCancel={handleCancel}
                footer={null}
            >
                {loading ? (
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        {props && (
                            <>
                                <div style={{ width: "100%" }}>
                                    <h3>Positive Documents Prompt</h3>
                                    <Input.TextArea
                                        rows={4}
                                        value={positivePrompt}
                                        onChange={(e) => setPositivePrompt(e.target.value)}
                                        placeholder="Enter your custom prompt for positive documents"
                                        style={{ marginBottom: "1rem" }}
                                    />
                                    <Button
                                        type="primary"
                                        onClick={() => handleLlmRequest(positivePrompt, positiveDocuments, "positive")}
                                        disabled={!positivePrompt || positiveDocuments.length === 0}
                                        style={{ marginBottom: "1rem" }}
                                        loading={buttonLoading.positive}
                                    >
                                        Ask AI for Positive Documents
                                    </Button>
                                    {renderSummary("positive")}
                                </div>
                                <div style={{ width: "100%" }}>
                                    <h3>Negative Documents Prompt</h3>
                                    <Input.TextArea
                                        rows={4}
                                        value={negativePrompt}
                                        onChange={(e) => setNegativePrompt(e.target.value)}
                                        placeholder="Enter your custom prompt for negative documents"
                                        style={{ marginBottom: "1rem" }}
                                    />
                                    <Button
                                        type="primary"
                                        onClick={() => handleLlmRequest(negativePrompt, negativeDocuments, "negative")}
                                        disabled={!negativePrompt || negativeDocuments.length === 0}
                                        style={{ marginBottom: "1rem" }}
                                        loading={buttonLoading.negative}
                                    >
                                        Ask AI for Negative Documents
                                    </Button>
                                    {renderSummary("negative")}
                                </div>
                            </>
                        )}
                        {!props && (
                            <div>
                                <h3>General Documents Prompt</h3>
                                <Input.TextArea
                                    rows={4}
                                    value={positivePrompt}
                                    onChange={(e) => setPositivePrompt(e.target.value)}
                                    placeholder="Enter your custom prompt"
                                    style={{ marginBottom: "1rem" }}
                                />
                                <Button
                                    type="primary"
                                    onClick={() => handleLlmRequest(positivePrompt, documents, "positive")}
                                    disabled={!positivePrompt || documents.length === 0}
                                    style={{ marginBottom: "1rem" }}
                                    loading={buttonLoading.positive}
                                >
                                    Ask AI
                                </Button>
                                {renderSummary("positive")}
                            </div>
                        )}
                        {props ? (
                            <div style={{ display: "flex", justifyContent: "space-around", width: "100%" }}>
                                <div>
                                    <h3>Positive Sentiments</h3>
                                    {renderDocumentList(positiveDocuments)}
                                </div>
                                <div>
                                    <h3>Negative Sentiments</h3>
                                    {renderDocumentList(negativeDocuments)}
                                </div>
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                }}
                            >
                                {renderDocumentList(documents)}
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default SentimentDocsTest;
